/**
 * Terminal Command System
 *
 * This module provides a modular, easily configurable command registry.
 * To add a new command:
 * 1. Create a CommandHandler function
 * 2. Add it to the COMMANDS object with the command name as key
 */

import { HOME_DIR, type FileNode } from '../Window/FileSystem';

export interface FileSystemInterface {
  listDirectory: (path: string) => FileNode[] | null;
  readFile: (path: string) => string | null;
  getNode: (path: string) => FileNode | null;
  resolvePath: (path: string, currentDir?: string) => string;
  getParentPath: (path: string) => string;
  currentPath: string;
  setCurrentPath: (path: string) => void;
}

export interface CommandContext {
  args: string[];
  history: HistoryEntry[];
  fileSystem?: FileSystemInterface;
  openWindow?: (windowId: string) => void;
  setTheme?: (theme: 'light' | 'dark' | 'system') => void;
  resolvedTheme?: string;
}

export interface InteractiveSession {
  steps: Array<{ prompt: string }>;
  onComplete: (values: string[]) => Promise<{ output: string | string[]; isError?: boolean }>;
  pendingMessage?: string;
}

export interface CommandResult {
  output: string | string[];
  isError?: boolean;
  interactive?: InteractiveSession;
}

export type CommandHandler = (ctx: CommandContext) => CommandResult;

export interface Command {
  handler: CommandHandler;
  description: string;
  usage?: string[];
}

export interface HistoryEntry {
  command: string;
  output: string[];
  isError?: boolean;
  noHistory?: boolean;
}

// ============================================
// Command Handlers
// ============================================

const helpCommand: CommandHandler = () => {
  const maxLen = Math.max(...Object.keys(COMMANDS).map((k) => k.length));
  const lines = Object.entries(COMMANDS).map(
    ([name, cmd]) => `  ${name.padEnd(maxLen)}  -  ${cmd.description}`
  );
  return {
    output: ['Available commands:', '', ...lines, ''],
  };
};

const whoamiCommand: CommandHandler = () => ({
  output: 'visitor',
});

const pwdCommand: CommandHandler = ({ fileSystem }) => ({
  output: fileSystem?.currentPath || HOME_DIR,
});

const lsCommand: CommandHandler = ({ args, fileSystem }) => {
  if (!fileSystem) {
    return { output: 'Error: File system not available', isError: true };
  }

  const showAll = args.includes('-a') || args.includes('-la') || args.includes('-al');
  const showLong = args.includes('-l') || args.includes('-la') || args.includes('-al');

  // Find non-flag argument for path
  const nonFlagArgs = args.filter((a) => !a.startsWith('-'));
  const targetArg = nonFlagArgs.length > 0 ? nonFlagArgs[0] : null;
  const resolvedPath = targetArg
    ? fileSystem.resolvePath(targetArg, fileSystem.currentPath)
    : fileSystem.currentPath;

  // Check if trying to access above home directory
  if (!resolvedPath.startsWith(HOME_DIR)) {
    return {
      output: `ls: cannot open directory '${targetArg || resolvedPath}': Permission denied`,
      isError: true,
    };
  }

  const items = fileSystem.listDirectory(resolvedPath);

  if (!items) {
    return {
      output: `ls: cannot access '${targetArg || resolvedPath}': No such file or directory`,
      isError: true,
    };
  }

  const formatItem = (node: FileNode): string => {
    if (showLong) {
      return `${node.permissions}  ${node.name}${node.type === 'directory' ? '/' : ''}`;
    }
    return node.name + (node.type === 'directory' ? '/' : '');
  };

  let output: string[] = [];

  if (showAll) {
    // Add . and .. entries
    output.push(showLong ? 'drwxr-xr-x  .' : '.');
    output.push(showLong ? 'drwxr-xr-x  ..' : '..');
  }

  // Filter out dotfiles/dotfolders unless -a flag is used
  const visibleItems = showAll ? items : items.filter((node) => !node.name.startsWith('.'));

  output = output.concat(visibleItems.map(formatItem));

  return { output };
};

const cdCommand: CommandHandler = ({ args, fileSystem }) => {
  if (!fileSystem) {
    return { output: 'Error: File system not available', isError: true };
  }

  const target = args.length === 0 || args[0] === '~' ? HOME_DIR : args[0];
  const resolvedPath = fileSystem.resolvePath(target, fileSystem.currentPath);

  // Check if trying to go above home directory
  if (!resolvedPath.startsWith(HOME_DIR)) {
    return {
      output: `cd: ${args[0] || '/'}: Permission denied`,
      isError: true,
    };
  }

  const node = fileSystem.getNode(resolvedPath);

  if (!node) {
    return {
      output: `cd: ${args[0]}: No such file or directory`,
      isError: true,
    };
  }

  if (node.type !== 'directory') {
    return {
      output: `cd: ${args[0]}: Not a directory`,
      isError: true,
    };
  }

  fileSystem.setCurrentPath(resolvedPath);
  return { output: '' };
};

const catCommand: CommandHandler = ({ args, fileSystem }) => {
  if (args.length === 0) {
    return { output: 'cat: missing file operand', isError: true };
  }

  if (!fileSystem) {
    return { output: 'Error: File system not available', isError: true };
  }

  const fileName = args[0];
  const resolvedPath = fileSystem.resolvePath(fileName, fileSystem.currentPath);

  // Check if trying to access above home directory
  if (!resolvedPath.startsWith(HOME_DIR)) {
    return {
      output: `cat: ${fileName}: Permission denied`,
      isError: true,
    };
  }

  const content = fileSystem.readFile(fileName);

  if (content !== null) {
    return { output: content.split('\n') };
  }

  // Check if it's a directory
  const node = fileSystem.getNode(fileName);
  if (node && node.type === 'directory') {
    return {
      output: `cat: ${fileName}: Is a directory`,
      isError: true,
    };
  }

  return {
    output: `cat: ${fileName}: No such file or directory`,
    isError: true,
  };
};

const echoCommand: CommandHandler = ({ args }) => ({
  output: args.join(' ') || '',
});

const dateCommand: CommandHandler = () => ({
  output: new Date().toString(),
});

const clearCommand: CommandHandler = () => ({
  output: '__CLEAR__', // Special signal handled by terminal
});

const historyCommand: CommandHandler = ({ history }) => {
  const navigable = history.filter((h) => h.command && !h.noHistory);
  if (navigable.length === 0) {
    return { output: 'No commands in history' };
  }
  return {
    output: navigable.map((entry, i) => `  ${i + 1}  ${entry.command}`),
  };
};

const aboutCommand: CommandHandler = () => ({
  output: [
    'Nick - Software Engineer & Solutions Architect',
    '',
    '---',
    '',
    'LinkedIn: https://linkedin.com/in/nicholas-bozentko',
    'GitHub:   https://github.com/nickbozentko',
  ],
});

const sudoCommand: CommandHandler = () => ({
  output: 'visitor is not in the sudoers file. This incident will be reported.',
  isError: true,
});

const neofetchCommand: CommandHandler = ({ resolvedTheme }) => {
  const theme = resolvedTheme || 'dark';
  const art = [
    '░░░░░░░░░░░░░░',
    '░N░░░N░░BBBB░░',
    '░NN░░N░░B░░░B░',
    '░N░N░N░░BBBB░░',
    '░N░░NN░░B░░░B░',
    '░N░░░N░░BBBB░░',
    '░░░░░░░░░░░░░░',
  ];
  const info = [
    'visitor@NickOS',
    '\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
    'OS:         NickOS 1.0.0 x86_64',
    'Shell:      mocksh 1.0.0',
    `Theme:      ${theme}`,
    'Terminal:   mockTerm',
    'Memory:     32039MB',
  ];
  return {
    output: art.map((line, i) => line + ' ' + (info[i] || '')),
  };
};

const treeCommand: CommandHandler = ({ args, fileSystem }) => {
  if (!fileSystem) {
    return { output: 'Error: File system not available', isError: true };
  }

  const nonFlagArgs = args.filter((a) => !a.startsWith('-'));
  const targetArg = nonFlagArgs.length > 0 ? nonFlagArgs[0] : null;
  const resolvedPath = targetArg
    ? fileSystem.resolvePath(targetArg, fileSystem.currentPath)
    : fileSystem.currentPath;

  if (!resolvedPath.startsWith(HOME_DIR)) {
    return { output: `tree: '${targetArg || resolvedPath}': Permission denied`, isError: true };
  }

  const rootNode = fileSystem.getNode(resolvedPath);
  if (!rootNode) {
    return { output: `tree: '${targetArg || resolvedPath}': No such file or directory`, isError: true };
  }
  if (rootNode.type !== 'directory') {
    return { output: `tree: '${targetArg || resolvedPath}': Not a directory`, isError: true };
  }

  const lines: string[] = [resolvedPath];

  function walk(dirPath: string, prefix: string) {
    const allChildren = fileSystem!.listDirectory(dirPath);
    if (!allChildren) return;

    // Hide dotfiles/dotfolders in tree output
    const children = allChildren.filter((child) => !child.name.startsWith('.'));

    children.forEach((child, i) => {
      const isLast = i === children.length - 1;
      const connector = isLast ? '\u2514\u2500\u2500 ' : '\u251c\u2500\u2500 ';
      const suffix = child.type === 'directory' ? '/' : '';
      lines.push(prefix + connector + child.name + suffix);

      if (child.type === 'directory') {
        const childPrefix = prefix + (isLast ? '    ' : '\u2502   ');
        walk(`${dirPath}/${child.name}`, childPrefix);
      }
    });
  }

  walk(resolvedPath, '');
  return { output: lines };
};

const manCommand: CommandHandler = ({ args }) => {
  if (args.length === 0) {
    return { output: 'usage: man <command>', isError: true };
  }

  const cmdName = args[0].toLowerCase();
  const command = COMMANDS[cmdName];

  if (!command || !command.usage) {
    return { output: `No manual entry for ${cmdName}`, isError: true };
  }

  return { output: command.usage };
};

const WINDOW_NAMES: Record<string, string> = {
  browser: 'browser',
  finder: 'finder',
  notepad: 'notepad',
  terminal: 'terminal',
  paint: 'paint',
};

const openCommand: CommandHandler = ({ args, openWindow }) => {
  if (args.length === 0) {
    return {
      output: `usage: open <app>\n\nAvailable: ${Object.keys(WINDOW_NAMES).join(', ')}`,
      isError: true,
    };
  }

  const appName = args[0].toLowerCase();
  const windowId = WINDOW_NAMES[appName];

  if (!windowId) {
    return {
      output: `open: unknown app '${args[0]}'. Available: ${Object.keys(WINDOW_NAMES).join(', ')}`,
      isError: true,
    };
  }

  if (openWindow) {
    openWindow(windowId);
  }

  const displayName = appName.charAt(0).toUpperCase() + appName.slice(1);
  return { output: `Opening ${displayName}...` };
};

const themeCommand: CommandHandler = ({ args, setTheme, resolvedTheme }) => {
  if (args.length === 0) {
    return { output: `Current theme: ${resolvedTheme || 'unknown'}` };
  }

  const value = args[0].toLowerCase();
  if (value !== 'light' && value !== 'dark' && value !== 'system') {
    return {
      output: `theme: invalid value '${args[0]}'. Use: light, dark, or system`,
      isError: true,
    };
  }

  if (setTheme) {
    setTheme(value);
  }

  return { output: `Theme set to: ${value}` };
};

/**
 * Send a contact message to the external API.
 * Replace the URL and payload shape with your actual endpoint.
 */
async function sendContactMessage(subject: string, body: string): Promise<{ ok: boolean }> {
  // TODO: Replace with your actual API endpoint and move this function
  const res = await fetch('https://api.example.com/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, body }),
  });
  return { ok: res.ok };
}

const contactCommand: CommandHandler = () => ({
  output: '',
  interactive: {
    steps: [{ prompt: 'Subject:' }, { prompt: 'Message:' }],
    pendingMessage: 'Sending...',
    onComplete: async (values: string[]) => {
      try {
        const result = await sendContactMessage(values[0], values[1]);
        if (!result.ok) {
          return { output: 'Failed to send message. Please try again.', isError: true };
        }
        return { output: "Message sent! I'll get back to you soon." };
      } catch {
        return { output: 'Failed to send message. Please try again.', isError: true };
      }
    },
  },
});

// ============================================
// Command Registry
// ============================================

/**
 * Command registry - add new commands here!
 * Key: command name (what user types)
 * Value: handler function
 */
export const COMMANDS: Record<string, Command> = {
  help: {
    handler: helpCommand,
    description: 'Show this help message',
    usage: [
      'usage: help',
      '',
      'Display a list of all available commands with descriptions.',
    ],
  },
  about: {
    handler: aboutCommand,
    description: 'About me',
    usage: [
      'usage: about',
      '',
      'Display information about the site owner.',
    ],
  },
  whoami: {
    handler: whoamiCommand,
    description: 'Display current user',
    usage: [
      'usage: whoami',
      '',
      'Print the current username.',
    ],
  },
  pwd: {
    handler: pwdCommand,
    description: 'Print working directory',
    usage: [
      'usage: pwd',
      '',
      'Print the absolute path of the current working directory.',
    ],
  },
  ls: {
    handler: lsCommand,
    description: 'List directory contents',
    usage: [
      'usage: ls [-l] [-a] [path]',
      '',
      'List the contents of a directory.',
      '',
      'Options:',
      '  -l    Use long listing format',
      '  -a    Show hidden files (., ..)',
      '',
      'Examples:',
      '  ls',
      '  ls -la',
      '  ls portfolio/projects',
    ],
  },
  cd: {
    handler: cdCommand,
    description: 'Change directory',
    usage: [
      'usage: cd [path]',
      '',
      'Change the current working directory.',
      '',
      'Examples:',
      '  cd portfolio',
      '  cd ..',
      '  cd ~',
    ],
  },
  cat: {
    handler: catCommand,
    description: 'Display file contents',
    usage: [
      'usage: cat <file>',
      '',
      'Print the contents of a file to the terminal.',
      '',
      'Examples:',
      '  cat README.md',
      '  cat portfolio/contact.txt',
    ],
  },
  echo: {
    handler: echoCommand,
    description: 'Print arguments to output',
    usage: [
      'usage: echo [text...]',
      '',
      'Print the given text to the terminal.',
      '',
      'Examples:',
      '  echo hello world',
    ],
  },
  date: {
    handler: dateCommand,
    description: 'Display current date and time',
    usage: [
      'usage: date',
      '',
      'Print the current date and time.',
    ],
  },
  clear: {
    handler: clearCommand,
    description: 'Clear the terminal',
    usage: [
      'usage: clear',
      '',
      'Clear all output from the terminal screen.',
    ],
  },
  history: {
    handler: historyCommand,
    description: 'Show command history',
    usage: [
      'usage: history',
      '',
      'Display a list of previously executed commands.',
    ],
  },
  tree: {
    handler: treeCommand,
    description: 'Display directory tree',
    usage: [
      'usage: tree [path]',
      '',
      'Recursively display the directory structure as a tree.',
      '',
      'Examples:',
      '  tree',
      '  tree portfolio',
    ],
  },
  neofetch: {
    handler: neofetchCommand,
    description: 'Display system information',
    usage: [
      'usage: neofetch',
      '',
      'Display system information with ASCII art.',
    ],
  },
  man: {
    handler: manCommand,
    description: 'Display command manual',
    usage: [
      'usage: man <command>',
      '',
      'Display detailed usage information for a command.',
      '',
      'Examples:',
      '  man ls',
      '  man cat',
    ],
  },
  open: {
    handler: openCommand,
    description: 'Open a window',
    usage: [
      'usage: open <app>',
      '',
      'Open a window by name.',
      'Available: browser, finder, notepad, terminal, paint',
      '',
      'Examples:',
      '  open browser',
      '  open finder',
    ],
  },
  theme: {
    handler: themeCommand,
    description: 'Get or set color theme',
    usage: [
      'usage: theme [light|dark|system]',
      '',
      'Get or set the color theme.',
      '',
      'With no arguments, prints the current theme.',
      'With an argument, sets the theme.',
      '',
      'Examples:',
      '  theme',
      '  theme dark',
      '  theme system',
    ],
  },
  sudo: {
    handler: sudoCommand,
    description: 'Execute as superuser',
    usage: [
      'usage: sudo <command>',
      '',
      'Execute a command with superuser privileges.',
      '',
      'Just kidding. You are not in the sudoers file.',
    ],
  },
  // contact: {
  //   handler: contactCommand,
  //   description: 'Send me a message',
  //   usage: [
  //     'usage: contact',
  //     '',
  //     'Start an interactive session to send a message.',
  //     'You will be prompted for a subject and message body.',
  //   ],
  // },
};

// ============================================
// Command Execution
// ============================================

/**
 * Execute a command string and return the result
 */
export function executeCommand(
  input: string,
  history: HistoryEntry[],
  fileSystem?: FileSystemInterface,
  context?: { openWindow?: (windowId: string) => void; setTheme?: (theme: 'light' | 'dark' | 'system') => void; resolvedTheme?: string }
): CommandResult {
  const trimmed = input.trim();

  if (!trimmed) {
    return { output: '' };
  }

  const parts = trimmed.split(/\s+/);
  const commandName = parts[0].toLowerCase();
  const args = parts.slice(1);

  const command = COMMANDS[commandName];

  if (!command) {
    return {
      output: `command not found: ${commandName}`,
      isError: true,
    };
  }

  return command.handler({
    args,
    history,
    fileSystem,
    openWindow: context?.openWindow,
    setTheme: context?.setTheme,
    resolvedTheme: context?.resolvedTheme,
  });
}

/**
 * Get list of available command names (for autocomplete, etc.)
 */
export function getCommandNames(): string[] {
  return Object.keys(COMMANDS).sort();
}
